import { Trash } from '@phosphor-icons/react';
import { useState } from 'react';
import translations from '../../../translations';
import { isNullOrWhitespace } from '../../../utils/guard';
import { stringReplace } from '../../../utils/string';
import Table, { type TableColumnList } from '../../generic/Table';
import type { UpdateTenant } from '../TenantPage';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';

type SAMLSettingsGroupsProps = { groups: string[]; updateTenant: UpdateTenant };

const SAMLSettingsGroups = ({ groups, updateTenant }: SAMLSettingsGroupsProps) => {
    const [groupToAdd, setGroupToAdd] = useState('');
    const dispatchNotification = useNotificationDispatch();

    const handleAddGroup = () => {
        if (isNullOrWhitespace(groupToAdd)) {
            dispatchNotification({
                type: 'notifyError',
                payload: { error: translations.SAML_groups_invalid_group_name_error_message },
            });
        } else if (groups.find((group) => group === groupToAdd)) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error: stringReplace(
                        translations.SAML_groups_duplicate_group_name_error_message,
                        {
                            group: groupToAdd,
                        },
                    ),
                },
            });
        } else {
            updateTenant({
                securitySettings: {
                    samlSettings: {
                        groupSettings: {
                            groups: [...groups, groupToAdd],
                        },
                    },
                },
            });
        }
    };

    const handleRemoveGroup = (groupToRemove: string) => {
        const filteredGroups = groups.filter((group) => group !== groupToRemove);

        updateTenant({
            securitySettings: {
                samlSettings: {
                    groupSettings: {
                        groups: filteredGroups,
                    },
                },
            },
        });
    };

    const columns: TableColumnList<string> = [
        {
            renderHeader: () => translations.COMMON_TABLE_group_name,
            renderCell: ({ item }) => item,
        },
        {
            renderHeader: () => translations.COMMON_TABLE_actions,
            renderCell: ({ item }) => (
                <div className="action-btn-wrapper">
                    <button
                        title={translations.SAML_groups_remove_group_button_label}
                        className="table-icon table-icon-delete"
                        aria-label={translations.SAML_groups_remove_group_button_label}
                        onClick={() => handleRemoveGroup(item)}
                        type="button"
                    >
                        <Trash />
                    </button>
                </div>
            ),
            size: '5rem',
        },
    ];

    return (
        <div className="form-group">
            <div className="restriction-widget">
                <label htmlFor="saml-groups-label">{translations.SAML_groups_label}</label>
                <div className="flex">
                    <input
                        id="saml-groups-label"
                        className="form-control"
                        type="text"
                        onChange={({ target: { value } }) => setGroupToAdd(value)}
                        placeholder={translations.SAML_groups_group_input_placeholder}
                    />
                    <button
                        className="btn btn-sm btn-success add-restriction"
                        onClick={handleAddGroup}
                        type="button"
                    >
                        {translations.COMMON_add}
                    </button>
                </div>
                <Table wrapperClassName="margin-top" columns={columns} items={groups} />
                <p className="help-block">
                    {translations.SAML_groups_help_1}
                    <br />
                    {translations.SAML_groups_help_2}
                </p>
            </div>
        </div>
    );
};

export default SAMLSettingsGroups;
